import React, { useState, useRef, useEffect, useCallback } from 'react'
import Swal from 'sweetalert2'
import { getSession } from '../../getSession'
import { getUserFromHashedList, massInsertNewTaskOrAppointment } from '../../utils/lead'

const AddTaskForm = ({
  onClose = () => { },
  onSave = () => { },
  users = [],
  initial = {},
  selectedCount = 0,
  selectedLeadIds = [],
  hashedIds = '',
  massOpBatchId = '',
  applyOnAllLeads = false,
  registerSubmitHandler
}) => {
  const normalizeCollaborators = (value) => {
    if (Array.isArray(value)) {
      return value
        .map(item => String(item ?? '').trim())
        .filter(Boolean)
    }

    if (value === null || value === undefined || value === '') return []

    return String(value)
      .split(',')
      .map(item => item.trim())
      .filter(Boolean)
  }

  const normalizeUsers = (list) => {
    const source = Array.isArray(list) ? list : []
    return source
      .map((rawUser, index) => {
        const preferredId = [
          rawUser?.id,
          rawUser?.Id,
          rawUser?.ID,
          rawUser?.UserId,
          rawUser?.Code,
          rawUser?.code,
          rawUser?.value,
          rawUser?.Value,
          rawUser?.ValueId,
          rawUser?.HashValue,
          rawUser?.HashId,
          rawUser?.User_Login,
          rawUser?.LoginName,
          rawUser?.UserName
        ]
          .map(item => String(item ?? '').trim())
          .find(item => item && item !== '0')

        const name = [
          rawUser?.name,
          rawUser?.Name,
          rawUser?.Text,
          rawUser?.TextN,
          rawUser?.DisplayName,
          rawUser?.FullName,
          rawUser?.label,
          rawUser?.Label
        ]
          .map(item => String(item ?? '').trim())
          .find(Boolean)

        const fallbackId = name || `user-${index}`
        return {
          id: preferredId || fallbackId,
          name: name || preferredId || fallbackId
        }
      })
      .filter(item => item.id)
  }

  const [title, setTitle] = useState(initial.title || '')
  const [description, setDescription] = useState(initial.description || '')
  const [remarks, setRemarks] = useState(initial.remarks || '')
  const [activityType, setActivityType] = useState(initial.activityType || 'Facebook Chat')
  const [dueAt, setDueAt] = useState(initial.dueAt || '')
  const [owner, setOwner] = useState(String(initial.owner || ''))
  const [collaborators, setCollaborators] = useState(() => normalizeCollaborators(initial.collaborators))
  const [showCollab, setShowCollab] = useState(false)
  const collabRef = useRef(null)
  const [schedule, setSchedule] = useState(initial.schedule || false)
  const [scheduleAt, setScheduleAt] = useState(initial.scheduleAt || '')
  const [error, setError] = useState('')
  const [fetchedUsers, setFetchedUsers] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const activityOptions = [
    'Facebook Chat',
    'Call',
    'Email',
    'Meeting'
  ]

  const fallbackUsers = [
    { id: '34594', name: 'Mohit.cheema (Mohit Cheema)' },
    { id: '34595', name: 'Manish.Singh (Surjit kaur)' },
    { id: '34596', name: 'Rachit.Kumar (naseem khan)' }
  ]

  const normalizedPropUsers = normalizeUsers(users)
  const userOptions = normalizedPropUsers.length
    ? normalizedPropUsers
    : (fetchedUsers.length ? fetchedUsers : fallbackUsers)

  const toggleCollaborator = (id) => {
    const collaboratorId = String(id)
    setCollaborators(prev => {
      const normalizedPrev = normalizeCollaborators(prev)
      return normalizedPrev.includes(collaboratorId)
        ? normalizedPrev.filter(item => item !== collaboratorId)
        : [...normalizedPrev, collaboratorId]
    })
  }

  useEffect(() => {
    let mounted = true

    if (normalizedPropUsers.length) {
      setFetchedUsers([])
      return () => { mounted = false }
    }

    const fetchUsers = async () => {
      try {
        const { userId, TokenId, token } = getSession()
        const payload = {
          Token: TokenId || token || '',
          Message: '',
          LoggedUserId: Number(userId) || 0,
          MAC_Address: '',
          IP_Address: '',
          Details: { UserId: Number(userId) || 0 },
          BroadcastName: ''
        }

        const response = await getUserFromHashedList(payload)
        let rawUsers = response?.Details ?? response?.d ?? response ?? []
        if (rawUsers && rawUsers.data) rawUsers = rawUsers.data
        if (rawUsers && !Array.isArray(rawUsers)) {
          rawUsers = rawUsers.ResultList ?? rawUsers.Users ?? rawUsers.UserList ?? rawUsers.Items ?? rawUsers.Data ?? rawUsers.Details ?? rawUsers
        }

        if (mounted) {
          setFetchedUsers(normalizeUsers(rawUsers))
        }
      } catch (fetchError) {
        console.warn('AddTaskForm user fetch error:', fetchError?.message || fetchError)
      }
    }

    fetchUsers()
    return () => { mounted = false }
  }, [normalizedPropUsers.length])

  useEffect(() => {
    if (!userOptions.length) return

    setCollaborators(prev => {
      const normalizedPrev = normalizeCollaborators(prev)
      const optionIds = new Set(userOptions.map(item => String(item.id)))
      return normalizedPrev.filter(item => optionIds.has(item))
    })

    setOwner(prevOwner => {
      const ownerId = String(prevOwner || '').trim()
      if (!ownerId) return ''
      return userOptions.some(item => String(item.id) === ownerId) ? ownerId : ''
    })
  }, [userOptions])

  useEffect(() => {
    const onDocClick = (e) => {
      if (collabRef.current && !collabRef.current.contains(e.target)) setShowCollab(false)
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [])

  const validate = () => {
    setError('')
    if (!title.trim()) { setError('Title is required'); return false }
    if (!dueAt && !schedule) { setError('Due Date and Time is required (or enable schedule)'); return false }
    if (schedule && !scheduleAt) { setError('Schedule date/time required when scheduling'); return false }
    return true
  }

  const handleSave = useCallback(async () => {
    if (!validate()) return

    const { userId, parentId, TokenId, token } = getSession()
    const numericUserId = Number(userId) || 0
    const numericParentId = Number(parentId) || 0
    const numericBatchId = Number(massOpBatchId) || 0
    const collaboratorIds = (Array.isArray(collaborators) ? collaborators : [])
      .map(id => String(id || '').trim())
      .filter(Boolean)
      .join(',')
    const idsFromSelection = (hashedIds || (Array.isArray(selectedLeadIds) ? selectedLeadIds.map(id => String(id)).filter(Boolean).join('/') : '') || '').trim()
    const shouldUseBatchId = !!applyOnAllLeads && numericBatchId > 0
    const idsForPayload = shouldUseBatchId ? '' : idsFromSelection

    if (!idsForPayload && !shouldUseBatchId) {
      setError('No leads selected')
      return
    }

    const requestPayload = {
      Token: TokenId || token || '',
      Message: '',
      LoggedUserId: numericUserId,
      MAC_Address: '',
      IP_Address: '',
      Details: {
        massInsertUpdateTaskaAppointment: {
          Id: 0,
          Title: title.trim(),
          Description: description || '',
          Remarks: remarks || '',
          Address: '',
          CompleteAddress: '',
          StartDate: null,
          EndDate: dueAt || null,
          AssignedTo: Number(owner) || numericUserId,
          CreatedBy: numericUserId,
          IsActive: true,
          TaskId: Number(activityType) || 0,
          Status: 'open',
          OutcomesId: 0,
          CollabratoerUserIds: collaboratorIds,
          IsSMS: false,
          IsEmail: false,
          IsTask: true,
          IsAppointment: false,
          TaskType: String(activityType || ''),
          LeadIds: idsForPayload,
          Ids: idsForPayload,
          Batchid: shouldUseBatchId ? numericBatchId : 0,
          Latitude: '',
          Longitude: '',
          NotifyMe: false,
          SmsSchedule: '',
          EmailSchedule: '',
          scheduledatetime: schedule ? String(scheduleAt || '') : ''
        },
        strUserID: String(numericUserId || ''),
        strParentID: String(numericParentId || ''),
        strTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || ''
      },
      BroadcastName: ''
    }

    try {
      setIsSubmitting(true)
      const response = await massInsertNewTaskOrAppointment(requestPayload)
      const status = Number(response?.Status ?? response?.status ?? 0)
      const message = response?.Message || ''
      const details = response?.Details
      const successMessage = typeof details === 'string' && details.trim()
        ? details
        : (message || 'Task created successfully.')

      if (status === 1) {
        Swal.fire({ icon: 'success', title: 'Success', text: successMessage })
        onSave(response)
        onClose()
        return
      }

      const errorMessage = message || (typeof details === 'string' ? details : '') || 'Failed to save task.'
      setError(errorMessage)
      Swal.fire({ icon: 'error', title: 'Error', text: errorMessage })
    } catch (err) {
      console.error('mass task save error', err)
      const errorMessage = err?.message || 'An error occurred while saving the task.'
      setError(errorMessage)
      Swal.fire({ icon: 'error', title: 'Error', text: errorMessage })
    } finally {
      setIsSubmitting(false)
    }
  }, [
    title,
    description,
    remarks,
    activityType,
    dueAt,
    owner,
    collaborators,
    schedule,
    scheduleAt,
    hashedIds,
    selectedLeadIds,
    massOpBatchId,
    applyOnAllLeads,
    onClose,
    onSave
  ])

  useEffect(() => {
    if (typeof registerSubmitHandler === 'function') {
      registerSubmitHandler(() => handleSave)
      return () => registerSubmitHandler(null)
    }
  }, [registerSubmitHandler, handleSave])

  return (
    <div className="w-full max-w-2xl">
      <div className="space-y-3">
        {selectedCount > 1 && (
          <div className="text-sm text-blue-700 bg-blue-50 p-3 rounded">
            Info! You are about to perform a mass action on {selectedCount} record(s)
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className='text-[#f00]'>*</span></label>
          <input value={title} onChange={e => setTitle(e.target.value)} className="w-full border px-3 py-2 rounded" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} className="w-full border px-3 py-2 rounded h-24 resize-none" placeholder="Start Typing the details about the task." />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
          <textarea value={remarks} onChange={e => setRemarks(e.target.value)} className="w-full border px-3 py-2 rounded h-20 resize-none" placeholder="about the task..." />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sales Activity Type</label>
          <select value={activityType} onChange={e => setActivityType(e.target.value)} className="w-full border px-3 py-2 rounded">
            {activityOptions.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Due Date and Time*</label>
          <input type="datetime-local" value={dueAt} onChange={e => setDueAt(e.target.value)} className="w-full border px-3 py-2 rounded" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
          <select value={owner} onChange={e => setOwner(e.target.value)} className="w-full border px-3 py-2 rounded">
            <option value="">Nothing selected</option>
            {userOptions.map(u => <option key={u.id} value={u.id}>{u.id}-{u.name}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Collaborators</label>
          <div className="relative" ref={collabRef}>
            <button
              type="button"
              onClick={() => setShowCollab(s => !s)}
              className="w-full text-left border rounded px-3 py-2 flex items-center justify-between"
            >
              <div className="truncate text-sm">
                {collaborators && collaborators.length > 0
                  ? userOptions.filter(u => collaborators.includes(u.id)).map(u => u.name).join(', ')
                  : 'Select collaborators'}
              </div>
              <div className="ml-2 text-gray-500">▾</div>
            </button>

            {showCollab && (
              <div className="absolute z-20 mt-1 w-full bg-white border rounded shadow max-h-48 overflow-auto">
                {userOptions.length > 0 ? (
                  userOptions.map(u => {
                    const selected = collaborators.includes(u.id)
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => toggleCollaborator(u.id)}
                        className={`w-full text-left px-3 py-2 flex items-center justify-between hover:bg-gray-100 ${selected ? 'bg-blue-50' : ''}`}
                      >
                        <div className="truncate text-sm">{u.id}-{u.name}</div>
                        {selected && <div className="text-blue-600">✓</div>}
                      </button>
                    )
                  })
                ) : (
                  <div className="text-sm text-gray-500 p-2">No users</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-2">
          <label className="inline-flex items-center">
            <input type="checkbox" checked={schedule} onChange={() => setSchedule(s => !s)} className=" rounded-[3px] mr-2" />
            On Schedule Date And Time
          </label>
        </div>

        {schedule && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CreateTask on Schedule Date and Time*</label>
            <input type="datetime-local" value={scheduleAt} onChange={e => setScheduleAt(e.target.value)} className="w-full border px-3 py-2 rounded" />
          </div>
        )}

        {error && <div className="text-red-600 text-sm">{error}</div>}
        {isSubmitting && <div className="text-sm text-gray-500">Saving task...</div>}

      </div>
    </div>
  )
}

export default AddTaskForm
